import { useEffect, useState, useRef, useCallback } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView(0.3);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1800;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const overviewAnim = useInView();
  const painPointsAnim = useInView();
  const featuresAnim = useInView();
  const dispatchAnim = useInView();
  const achievementsAnim = useInView();
  const dashboardAnim = useInView();
  const mobileAnim = useInView();
  const customersAnim = useInView();
  const contactAnim = useInView();
  const statsAnim = useInView(0.2);

  const painPoints = [
    { icon: 'ri-gas-station-line', title: '加油费说不清', desc: '司机加了多少油、在哪加的、油钱对不上账，月底核查只能靠感觉' },
    { icon: 'ri-tools-line', title: '维修费无底洞', desc: '修车没有审批，钱花出去不知道修了什么，同一个问题反复修' },
    { icon: 'ri-dashboard-line', title: '里程数没法查', desc: '不知道每辆车跑了多少公里，油耗算不出来，车辆寿命无从评估' },
    { icon: 'ri-phone-line', title: '调度靠电话', desc: '派车全凭口头通知，谁开哪辆车，事后扯皮无据可查' },
    { icon: 'ri-calendar-line', title: '证件过期没提醒', desc: '年检、保险到期才发现，一旦出事合规责任难以承担' },
    { icon: 'ri-alert-line', title: '违章无人管', desc: '不知道谁违章了多少次，司机绩效考核缺乏客观依据' },
    { icon: 'ri-question-line', title: '数据全靠感觉', desc: '老板不知道今天跑了多少趟、赚了多少、亏了多少' },
    { icon: 'ri-file-list-line', title: '包车账目算不清', desc: '旅游包车途中加油、过路费、餐费各算各的，行程结束后费用汇总全靠司机口头报账，客户一质疑就无从核对' },
  ];

  const featureSellPoints = [
    {
      title: '加油成本精准管控',
      subtitle: '堵住每一个加油漏洞',
      desc: '每次加油记录时间、地点、升数、金额、发票照片，全面留档。内置OCR图像识别，司机拍摄加油机照片后系统自动识别油量并填入，从源头杜绝数字造假。支持内部与外部加油站分类记录，月度账目一键比对。百公里油耗自动计算，耗油TOP3车辆实时排名，近30天趋势图异常波动第一时间暴露。',
      image: 'images/3-image.jpg',
      features: ['OCR自动识别油量', '百公里油耗计算', '内外加油站分类', '30天趋势分析'],
      reverse: false
    },
    {
      title: '维修保养审批闭环',
      subtitle: '每一分维修费用都有完整链路',
      desc: '系统内置维修保养申请→审批→结案完整流程，杜绝先修后报带来的财务漏洞。维修项目按条目明细记录，更换了什么零件清晰可查。百公里维修成本自动统计，维修费用最高TOP3车辆排名。轮胎独立管理：位置、品牌、DOT编码、生产年份全记录，更换历史完整可追溯。手机APP提交申请，PC端审批，无论人在哪里流程不中断。',
      image: 'images/4-image.jpg',
      features: ['完整审批流程', '维修明细记录', '轮胎独立管理', 'APP+PC双端'],
      reverse: true
    },
    {
      title: '行车日志数字化',
      subtitle: '每一趟班车的轨迹都有据可查',
      desc: '班次、路线、司机、车辆四要素关联绑定。出车前拍摄仪表盘，OCR自动识别里程数，收车后同样留存照片，本次行驶里程系统自动计算。GPS精确记录停车点、始发站、终点站、收车点的坐标与到达时间，还原完整行车轨迹。是否走高速、是否替班全程记录，费用核算有充分依据。',
      image: 'images/5-image.jpg',
      features: ['四要素关联绑定', 'OCR识别里程', 'GPS轨迹记录', '高速替班记录'],
      reverse: false
    },
    {
      title: '旅游包车专项管理',
      subtitle: '包车业务账目清晰，再也不怕纠纷',
      desc: '每次包车建立独立档案，记录用车单位、目的地、行程天数。中途停靠站支持多次添加，行程路线完整留存。出车/收车里程照片自动存档，里程系统计算。途中每次加油均关联绑定至本次包车记录，餐费、高速费、货运费分项录入，财务对账零误差，客户核账有理有据。',
      image: 'images/6-image.jpg',
      features: ['独立包车档案', '停靠站多次添加', '加油关联绑定', '费用分项录入'],
      reverse: true
    },
    {
      title: '车辆全生命周期管理',
      subtitle: '一辆车从购入到报废，档案全在系统里',
      desc: '每辆车独立建档，累计行驶里程随每次行车自动更新，无需人工维护。年检、保险到期提前预警，杜绝逾期上路和车辆脱保风险。维修、加油、包车、违章、事故历史全部汇聚在一张车辆档案页，一车一档，历史一览无余。',
      image: 'images/7-image.jpg',
      features: ['一车一档管理', '里程自动更新', '年检保险预警', '历史一览无余'],
      reverse: false
    },
    {
      title: '安全生产可视化管控',
      subtitle: '把安全风险关在笼子里',
      desc: '违章信息逐条录入，累计次数自动排名，高风险车辆一眼识别。运营异常独立记录，每车异常次数统计，识别高风险运营状态。行管超速次数量化统计，为司机月度绩效考核提供客观依据，告别主观评价引发的劳动争议。事故信息从发生到处理结案全流程档案化管理。',
      image: 'images/8-image.jpg',
      features: ['违章自动排名', '运营异常统计', '超速量化考核', '事故全流程管理'],
      reverse: true
    },
    {
      title: '智能调度派单',
      subtitle: '派车更快、更准，再也不扯皮',
      desc: '发起派单时系统自动过滤不匹配车辆（油槽车不参与客运派单），每辆车的默认司机自动排首位，减少选择成本。派单信息全部留档，谁派的车、派给谁、什么时间，事后查询有据可依。司机手机APP实时接收派单通知，调度全过程线上留存，彻底告别电话派车口说无凭。',
      image: 'images/9-image.jpg',
      features: ['车辆智能过滤', '默认司机优先', '派单全程留档', 'APP实时通知'],
      reverse: false
    },
    {
      title: '乘客端班车查询',
      subtitle: '让乘客自己查，减少大量电话咨询',
      desc: '乘客绑定所在企业，输入起点终点，系统智能匹配可乘路线，展示上班/下班双向班次，发车状态实时更新，车辆实时位置可查。乘客自助查询，既减少管理员重复回复压力，又提升出行体验。',
      image: 'images/10-image.jpg',
      features: ['智能路线匹配', '双向班次展示', '实时位置查询', '自助减少咨询'],
      reverse: true
    },
    {
      title: '经营数据看板',
      subtitle: '老板随时掌握公司全局，无需开口问',
      desc: '今日出车趟数、行驶里程、加油量、维修费用四大核心指标实时更新，全车队GPS位置一屏展示。耗油、维修成本、违章、超速四维车辆排名，哪辆车是成本黑洞数据一眼识别。近30天加油量与百公里油耗双趋势图，经营走势可视可预判。PC端与手机APP完全同步，出差途中同样掌握全局。',
      image: 'images/11-image.jpg',
      features: ['四大核心指标', '四维车辆排名', '30天趋势图', 'PC+APP同步'],
      reverse: false
    },
    {
      title: '消息实时推送',
      subtitle: '审批不延误，业务不断链',
      desc: '维修申请提交、审批通过、驳回等关键节点，相关人员手机即时收到推送通知。支持华为、小米、OPPO、vivo等主流厂商推送通道，消息必达。站内信与手机通知双重保障，重要事项零遗漏。',
      image: 'images/12-image.jpg',
      features: ['关键节点推送', '多厂商通道', '站内信双保障', '重要事项必达'],
      reverse: true
    }
  ];

  const customerTypes = [
    {
      title: '企事业单位员工班车运营公司',
      needs: '多线路、多班次、司机考勤与行车日志精细管理',
      icon: 'ri-building-line',
      image: 'images/13-image.jpg',
    },
    {
      title: '旅游包车 / 大巴租赁公司',
      needs: '包车费用核算、行程留档、客户纠纷有据可查',
      icon: 'ri-map-pin-line',
      image: 'images/14-image.jpg',
    },
    {
      title: '拥有自营车队的大中型企业',
      needs: '车辆成本管控、维修审批、年检保险合规提醒',
      icon: 'ri-car-line',
      image: 'images/15-image.jpg',
    },
    {
      title: '需要对司机进行绩效考核的管理者',
      needs: '违章次数、超速记录、行车里程数据化考核依据',
      icon: 'ri-bar-chart-box-line',
      image: 'images/16-image.jpg',
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-18px); }
        }
        @keyframes float2 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(5deg); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(48px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-left {
          from { opacity: 0; transform: translateX(-48px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slide-right {
          from { opacity: 0; transform: translateX(48px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes bounce-in {
          0% { opacity: 0; transform: scale(0.3); }
          50% { transform: scale(1.08); }
          70% { transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes warning-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 109, 0, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(255, 109, 0, 0); }
        }
        @keyframes particle-float {
          0%, 100% { transform: translate(0, 0); opacity: 0.3; }
          25% { transform: translate(10px, -10px); opacity: 0.6; }
          50% { transform: translate(-5px, -20px); opacity: 0.9; }
          75% { transform: translate(-15px, -10px); opacity: 0.6; }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(30, 136, 229, 0.15); }
          50% { box-shadow: 0 0 30px rgba(30, 136, 229, 0.3); }
        }
        .animate-float { animation: float 4s ease-in-out infinite; }
        .animate-float2 { animation: float2 5s ease-in-out infinite; }
        .animate-float-delay { animation: float 4s ease-in-out 1.5s infinite; }
        .animate-pulse-ring { animation: pulse-ring 2s ease-out infinite; }
        .animate-spin-slow { animation: spin-slow 12s linear infinite; }
        .animate-warning-pulse { animation: warning-pulse 2s ease-in-out infinite; }
        .animate-particle-float { animation: particle-float 4s ease-in-out infinite; }
        .animate-glow-pulse { animation: glow-pulse 2s ease-in-out infinite; }
        .hero-title { animation: slide-up 0.9s cubic-bezier(.22,1,.36,1) both; }
        .hero-sub { animation: slide-up 0.9s cubic-bezier(.22,1,.36,1) 0.2s both; }
        .hero-desc { animation: slide-up 0.9s cubic-bezier(.22,1,.36,1) 0.35s both; }
        .hero-btns { animation: slide-up 0.9s cubic-bezier(.22,1,.36,1) 0.5s both; }
        .hero-badge { animation: bounce-in 0.8s cubic-bezier(.22,1,.36,1) 0.7s both; }
        .in-view-up { animation: slide-up 0.75s cubic-bezier(.22,1,.36,1) both; }
        .in-view-left { animation: slide-left 0.75s cubic-bezier(.22,1,.36,1) both; }
        .in-view-right { animation: slide-right 0.75s cubic-bezier(.22,1,.36,1) both; }
        .in-view-scale { animation: scale-in 0.65s cubic-bezier(.22,1,.36,1) both; }
        .in-view-fade { animation: fade-in 0.8s ease both; }
        .card-hover { transition: transform 0.35s cubic-bezier(.22,1,.36,1), box-shadow 0.35s ease; }
        .card-hover:hover { transform: translateY(-10px) scale(1.02); box-shadow: 0 24px 48px rgba(0,0,0,0.12); }
        .shimmer-text {
          background: linear-gradient(90deg, #0A2463 0%, #1E88E5 40%, #00E5FF 60%, #0A2463 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }
        .nav-link-underline { position: relative; }
        .nav-link-underline::after {
          content: ''; position: absolute; bottom: -2px; left: 0; width: 0; height: 2px;
          background: #1E88E5; transition: width 0.3s ease;
        }
        .nav-link-underline:hover::after { width: 100%; }
        .glass-card {
          background: rgba(255,255,255,0.92);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.6);
        }
        .step-line::before {
          content: ''; position: absolute; left: 24px; top: 48px; bottom: -32px;
          width: 2px; background: linear-gradient(to bottom, #1E88E5, transparent);
        }
        .particle-line {
          position: absolute;
          width: 2px;
          height: 60px;
          background: linear-gradient(to bottom, transparent, #1E88E5, transparent);
          opacity: 0.3;
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className={`flex items-center gap-3 transition-all duration-500 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
            <img src="images/logo.png" alt="数智运联" className="h-10 w-auto" />
            <span className={`text-2xl font-bold transition-colors duration-300 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>数智运联</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {['overview','painpoints','features','dispatch','dashboard','mobile','customers','contact'].map((id, i) => {
              const labels: Record<string, string> = { overview:'产品概述', painpoints:'痛点共鸣', features:'核心功能', dispatch:'智能调度', dashboard:'数据看板', mobile:'移动端', customers:'适用客户', contact:'联系我们' };
              return (
                <button key={id} onClick={() => scrollToSection(id)}
                  style={{ transitionDelay: `${i * 60}ms` }}
                  className={`text-sm font-medium transition-all duration-300 nav-link-underline whitespace-nowrap cursor-pointer ${
                    heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                  } ${isScrolled ? 'text-gray-700 hover:text-[#1E88E5]' : 'text-white/90 hover:text-white'}`}>
                  {labels[id]}
                </button>
              );
            })}
          </div>
          <button onClick={() => scrollToSection('contact')}
            className={`hidden md:block px-6 py-2.5 bg-[#1E88E5] text-white text-sm font-medium rounded-full hover:bg-[#0A2463] transition-all duration-300 hover:scale-105 hover:shadow-lg whitespace-nowrap cursor-pointer ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
            免费试用
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" onMouseMove={handleMouseMove}>
        <div className="absolute inset-0">
          <img
            src="images/1-image.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover scale-105"
            style={{ transform: `scale(1.05) translate(${mousePos.x * 0.005}px, ${mousePos.y * 0.005}px)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A2463]/55 via-[#0D1B2A]/45 to-[#0A2463]/55"></div>
        </div>

        <div className="absolute top-1/4 left-1/6 w-64 h-64 bg-[#1E88E5]/20 rounded-full blur-3xl animate-float pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-[#00E5FF]/15 rounded-full blur-3xl animate-float-delay pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full animate-spin-slow pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-[#00E5FF]/10 rounded-full animate-spin-slow pointer-events-none" style={{ animationDirection: 'reverse', animationDuration: '8s' }}></div>

        <div className={`relative z-10 text-center px-6 max-w-5xl mx-auto w-full transition-all duration-700 ${heroVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="pt-20 md:pt-0">
          <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1E88E5]/20 border border-[#00E5FF]/40 rounded-full mb-6 md:mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-pulse"></span>
            <span className="text-[#00E5FF] text-xs md:text-sm font-medium">全新一代车队数字化管理平台</span>
          </div>

          <h1 className="hero-title text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
            数智运联 · 让班车公司的每一分钱<br />都花得明明白白
          </h1>
          <p className="hero-sub text-lg md:text-2xl lg:text-3xl text-white/90 mb-3 md:mb-4 font-medium">
            车辆 · 加油 · 维修 · 行车 · 包车 · 调度 · 安全 · 考勤
          </p>
          <p className="hero-desc text-sm md:text-lg lg:text-xl text-white/80 mb-8 md:mb-12 max-w-4xl mx-auto leading-relaxed">
            全业务链数字化管理，一个系统搞定班车公司所有运营难题。<br />
            传统班车公司的痛点不在于缺人、缺车，而在于<span className="shimmer-text font-semibold">缺数据</span>。<br />
            加油对不上账、维修没有审批、里程不知真假、调度靠嘴说……<br />
            数智运联将这一切变成可记录、可查询、可分析的数字资产，让管理者真正做到心中有数。
          </p>

          <div className="hero-btns flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => scrollToSection('contact')}
              className="group relative px-8 py-4 bg-[#1E88E5] text-white text-base font-medium rounded-full hover:bg-[#00E5FF] hover:text-[#0A2463] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#00E5FF]/40 whitespace-nowrap cursor-pointer overflow-hidden">
              <span className="relative z-10 flex items-center gap-2">
                免费申请体验
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </span>
            </button>
            <button onClick={() => scrollToSection('overview')}
              className="group px-8 py-4 bg-transparent border-2 border-white/60 text-white text-base font-medium rounded-full hover:bg-white/15 hover:border-white transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-2">
              查看功能介绍
              <i className="ri-play-circle-line text-lg transition-transform duration-300 group-hover:scale-110"></i>
            </button>
          </div>

          <div className="flex flex-col items-center gap-2 animate-bounce mt-8">
            <span className="text-white/50 text-xs">向下滚动</span>
            <i className="ri-arrow-down-line text-white/50 text-xl"></i>
          </div>
          </div>
        </div>
      </section>

      {/* Stats Bar - 白色半透明 */}
      <section className="py-12 px-6 bg-white/80 backdrop-blur-md border-y border-gray-100 relative overflow-hidden">
        <div ref={statsAnim.ref} className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
          {[
            { value: 500, suffix: '+', label: '服务企业' },
            { value: 50000, suffix: '+', label: '管理车辆' },
            { value: 11, suffix: '', label: '核心功能模块' },
            { value: 99, suffix: '%', label: '客户满意度' },
          ].map((stat, i) => (
            <div key={i} className={`text-center transition-all duration-700 ${statsAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="text-4xl font-bold text-[#0A2463] mb-1">
                {statsAnim.inView ? <AnimatedCounter target={stat.value} suffix={stat.suffix} /> : `0${stat.suffix}`}
              </div>
              <div className="text-gray-500 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Product Overview */}
      <section id="overview" className="py-24 px-6 bg-white">
        <div ref={overviewAnim.ref} className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className={`order-2 md:order-1 transition-all duration-800 ${overviewAnim.inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
              style={{ transitionDuration: '0.8s' }}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                <img
                  src="images/2-image.jpg"
                  alt="产品界面"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2463]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>

            <div className={`order-1 md:order-2 transition-all duration-800 ${overviewAnim.inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
              style={{ transitionDuration: '0.8s', transitionDelay: '0.15s' }}>
              <div className="inline-block px-4 py-1.5 bg-[#1E88E5]/10 text-[#1E88E5] text-xs font-semibold rounded-full mb-6">
                ABOUT PRODUCT
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                全生命周期<br /><span className="shimmer-text">数字化管理</span>
              </h2>
              <p className="text-xl text-gray-600 mb-12">让车队运营更透明、更高效</p>

              <div className="grid grid-cols-2 gap-8">
                {[
                  { icon: 'ri-shield-check-line', title: '加油成本精准管控', desc: '堵住每一个加油漏洞' },
                  { icon: 'ri-tools-line', title: '维修保养审批闭环', desc: '每一分维修费用都有完整链路' },
                  { icon: 'ri-dashboard-line', title: '行车日志数字化', desc: '每一趟班车的轨迹都有据可查' },
                  { icon: 'ri-phone-line', title: '旅游包车专项管理', desc: '包车业务账目清晰，再也不怕纠纷' },
                  { icon: 'ri-car-line', title: '车辆全生命周期管理', desc: '一车一档，历史一览无余' },
                  { icon: 'ri-alert-line', title: '安全生产可视化管控', desc: '把安全风险关在笼子里' },
                  { icon: 'ri-play-circle-line', title: '智能调度派单', desc: '派车更快、更准，再也不扯皮' },
                  { icon: 'ri-mail-line', title: '乘客端班车查询', desc: '让乘客自己查，减少大量电话咨询' },
                  { icon: 'ri-line-chart-line', title: '经营数据看板', desc: '老板随时掌握公司全局' },
                  { icon: 'ri-notification-line', title: '消息实时推送', desc: '审批不延误，业务不断链' },
                ].map((item, i) => (
                  <div key={i} className={`group transition-all duration-600 ${overviewAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${0.3 + i * 0.1}s` }}>
                    <div className="w-12 h-12 flex items-center justify-center bg-[#1E88E5]/10 rounded-xl mb-3 transition-all duration-300 group-hover:bg-[#1E88E5] group-hover:scale-110">
                      <i className={`${item.icon} text-2xl text-[#1E88E5]`}></i>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>

              <button onClick={() => scrollToSection('features')}
                className="mt-10 px-6 py-3 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-[#1E88E5] transition-all duration-300 inline-flex items-center gap-2 whitespace-nowrap cursor-pointer hover:scale-105 hover:shadow-lg group">
                了解更多功能
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section - 浅蓝色系背景 */}
      <section id="painpoints" className="py-24 px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 40%, #E1F5FE 70%, #B3E5FC 100%)' }}>
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <img
            src="images/22-image.jpg"
            alt="背景"
            className="w-full h-full object-cover"
          />
        </div>

        <div ref={painPointsAnim.ref} className="max-w-7xl mx-auto relative z-10">
          <div className={`text-center mb-6 transition-all duration-700 ${painPointsAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A2463] mb-4">您是否也有这些烦恼？</h2>
            <p className="text-lg text-[#1565C0] max-w-4xl mx-auto">
              在与众多班车公司交流后，我们发现以下问题几乎无处不在，<br />
              如果您深有同感，数智运联正是为您而生。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {painPoints.map((point, i) => (
              <div
                key={i}
                className={`relative bg-white/75 backdrop-blur-sm rounded-2xl p-6 border border-[#FF6D00]/25 transition-all duration-700 hover:scale-105 hover:border-[#FF6D00]/50 group shadow-sm hover:shadow-lg ${
                  painPointsAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-[#FF6D00]/3 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#FF6D00]/15 rounded-xl transition-all duration-300 group-hover:bg-[#FF6D00]/25 animate-warning-pulse">
                    <i className={`${point.icon} text-3xl text-[#FF6D00]`}></i>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-[#0A2463] mb-3 group-hover:text-[#1E88E5] transition-colors duration-300">
                  {point.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">{point.desc}</p>

                <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-[#FF6D00]/15 rounded-full border border-[#FF6D00]/40">
                  <span className="text-xs font-bold text-[#FF6D00]">{i + 1}</span>
                </div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-16 transition-all duration-700 ${painPointsAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.8s' }}>
            <p className="text-xl text-[#0A2463] mb-6">
              这些问题，<span className="text-[#1E88E5] font-semibold">数智运联</span>都能帮您解决
            </p>
            <button
              onClick={() => scrollToSection('features')}
              className="px-8 py-4 bg-[#1E88E5] text-white text-base font-medium rounded-full hover:bg-[#0A2463] transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center gap-2 group"
            >
              查看解决方案
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Core Features Sell Points */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div ref={featuresAnim.ref} className={`text-center mb-20 transition-all duration-700 ${featuresAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-block px-4 py-1.5 bg-[#0A2463]/10 text-[#0A2463] text-xs font-semibold rounded-full mb-6">
              CORE FEATURES
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              一套系统，覆盖班车公司全部管理场景
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              从司机手机到管理后台，从每一升油到每一次维修，数智运联把班车公司的日常运营动作全部沉淀为数据资产，让成本看得见、风险管得住、决策有依据。
            </p>
          </div>

          <div className="space-y-32">
            {featureSellPoints.map((point, index) => (
              <FeatureSellPoint key={index} point={point} />
            ))}
          </div>
        </div>
      </section>

      {/* Suitable Customers Section */}
      <section id="customers" className="py-24 px-6 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E88E5]/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00E5FF]/5 rounded-full blur-3xl pointer-events-none"></div>

        <div ref={customersAnim.ref} className="max-w-7xl mx-auto relative z-10">
          <div className={`text-center mb-6 transition-all duration-700 ${customersAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-block px-4 py-1.5 bg-[#1E88E5]/10 text-[#1E88E5] text-xs font-semibold rounded-full mb-6">
              WHO WE SERVE
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">数智运联适合哪些客户？</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              无论您管理5辆车还是500辆车，数智运联都能为您提供清晰的数字化运营视角。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            {customerTypes.map((customer, i) => (
              <div
                key={i}
                className={`group relative overflow-hidden rounded-3xl transition-all duration-700 card-hover ${
                  customersAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${i * 0.15}s` }}
              >
                <div className="relative h-80 overflow-hidden">
                  <img src={customer.image} alt={customer.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A2463]/90 via-[#0A2463]/60 to-[#0A2463]/30 transition-opacity duration-500 group-hover:from-[#1E88E5]/90 group-hover:via-[#1E88E5]/60"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#00E5FF]/20 backdrop-blur-sm rounded-xl mb-4 border border-[#00E5FF]/40 transition-all duration-300 group-hover:bg-[#00E5FF]/30 group-hover:scale-110">
                    <i className={`${customer.icon} text-3xl text-[#00E5FF]`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 transition-colors duration-300 group-hover:text-[#00E5FF]">
                    {customer.title}
                  </h3>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-base text-[#B0BEC5] leading-relaxed">{customer.needs}</p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E88E5]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-16 transition-all duration-700 ${customersAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.6s' }}>
            <p className="text-xl text-gray-600 mb-6">找到您的场景了吗？立即体验数智运联带来的管理变革</p>
            <button onClick={() => scrollToSection('contact')}
              className="px-8 py-4 bg-[#1E88E5] text-white text-base font-medium rounded-full hover:bg-[#0A2463] transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center gap-2 group">
              免费申请体验
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Digital Achievements Section - 浅蓝色系背景 */}
      <section className="py-24 px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 40%, #E1F5FE 70%, #B3E5FC 100%)' }}>
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="particle-line animate-particle-float"
              style={{ left: `${(i * 5.3) % 100}%`, top: `${(i * 7.1) % 100}%`, animationDelay: `${(i * 0.3) % 4}s`, animationDuration: `${3 + (i % 3)}s` }} />
          ))}
          <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-[#1E88E5]/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/6 w-96 h-96 bg-[#00E5FF]/10 rounded-full blur-3xl animate-float-delay"></div>
        </div>

        <div ref={achievementsAnim.ref} className="max-w-7xl mx-auto relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${achievementsAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-block px-4 py-1.5 bg-[#1E88E5]/15 border border-[#1E88E5]/30 text-[#1E88E5] text-xs font-semibold rounded-full mb-6">
              用数据说话
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A2463] mb-4">核心技术优势</h2>
            <p className="text-xl text-[#1565C0] max-w-3xl mx-auto">
              全方位数字化能力，为班车公司提供完整的管理解决方案
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-apps-line', number: '10+', label: '核心管理模块', desc: '覆盖班车公司全业务链', color: 'from-[#1E88E5] to-[#00E5FF]' },
              { icon: 'ri-device-line', number: 'PC + APP', label: '双端同步', desc: '管理后台与手机应用实时同步', color: 'from-[#00E5FF] to-[#1E88E5]' },
              { icon: 'ri-checkbox-circle-line', number: '全流程', label: '线上审批', desc: '加油/维修/轮胎/采购四大申请闭环', color: 'from-[#1E88E5] to-[#0A2463]' },
              { icon: 'ri-map-pin-line', number: '实时', label: 'GPS定位', desc: '全车队位置一屏掌控', color: 'from-[#00E5FF] to-[#1E88E5]' },
              { icon: 'ri-scan-line', number: 'OCR', label: '智能识别', desc: '里程/油量拍照自动识别，录入效率大幅提升', color: 'from-[#1E88E5] to-[#00E5FF]' },
              { icon: 'ri-notification-line', number: '多厂商', label: '推送覆盖', desc: '华为/小米/OPPO/vivo全覆盖，消息稳定送达', color: 'from-[#1E88E5] to-[#0A2463]' },
            ].map((item, i) => (
              <div key={i} className={`relative group transition-all duration-700 ${achievementsAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#1E88E5]/10 to-[#00E5FF]/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-white/80 backdrop-blur-sm border border-[#1E88E5]/20 rounded-2xl p-8 hover:border-[#1E88E5]/50 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-glow-pulse">
                  <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-xl mb-6 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <div className={`text-4xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-2`}>{item.number}</div>
                  <h3 className="text-xl font-bold text-[#0A2463] mb-3">{item.label}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-[#1E88E5]/30 rounded-tr-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-[#1E88E5]/30 rounded-bl-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
            ))}
          </div>

          <div className={`mt-16 text-center transition-all duration-700 ${achievementsAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.6s' }}>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/60 border border-[#1E88E5]/30 rounded-full backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#00E676] rounded-full animate-pulse"></div>
                <span className="text-gray-600 text-sm">系统稳定运行</span>
              </div>
              <div className="w-px h-4 bg-[#1E88E5]/30"></div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#00E676] rounded-full animate-pulse"></div>
                <span className="text-gray-600 text-sm">7×24小时技术支持</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Dispatch */}
      <section id="dispatch" className="py-24 px-6 bg-white">
        <div ref={dispatchAnim.ref} className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-800 ${dispatchAnim.inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
              style={{ transitionDuration: '0.8s' }}>
              <div className="inline-block px-4 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-full mb-6">
                SMART DISPATCH
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">智能调度派单</h2>
              <p className="text-2xl text-gray-600 mb-3">让派车更快更准</p>
              <p className="text-lg text-gray-500 mb-12">AI算法自动匹配最优方案</p>

              <div className="space-y-8">
                {[
                  { num: '1', title: '智能匹配', desc: '系统自动匹配最合适的车辆和司机，默认司机自动排首位，大幅提升派单效率' },
                  { num: '2', title: '类型过滤', desc: '油槽车与客运车自动区分，防止派错车型，确保运营安全合规' },
                  { num: '3', title: '全程跟踪', desc: '派单状态全程跟踪，从待派到完成一目了然，手机端随时查看和接收' },
                ].map((step, i) => (
                  <div key={i} className={`flex gap-6 relative step-line last:before:hidden transition-all duration-600 ${dispatchAnim.inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${0.2 + i * 0.15}s` }}>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1E88E5] text-white text-xl font-bold rounded-full shadow-lg shadow-[#1E88E5]/20 transition-all duration-300 hover:scale-110">
                        {step.num}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => scrollToSection('contact')}
                className="mt-10 px-6 py-3 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-[#1E88E5] transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 hover:shadow-lg">
                查看调度演示
              </button>
            </div>

            <div className={`relative transition-all duration-800 ${dispatchAnim.inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
              style={{ transitionDuration: '0.8s', transitionDelay: '0.2s' }}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                <img
                  src="images/19-image.jpg"
                  alt="智能调度界面"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#1E88E5]/20 rounded-full blur-3xl animate-float"></div>
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-[#00E5FF]/20 rounded-full blur-3xl animate-float-delay"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Dashboard - 浅蓝色系背景 */}
      <section id="dashboard" className="py-24 px-6 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E88E5]/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00E5FF]/5 rounded-full blur-3xl animate-float-delay"></div>
        <div ref={dashboardAnim.ref} className="max-w-7xl mx-auto relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${dashboardAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A2463] mb-4">经营决策一屏掌控</h2>
            <p className="text-xl text-[#1565C0]">实时数据看板，让管理者随时掌握运营全局</p>
          </div>

          <div className={`relative transition-all duration-900 ${dashboardAnim.inView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            style={{ transitionDuration: '0.9s', transitionDelay: '0.2s' }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="images/18-image.jpg"
                alt="数据看板"
                className="w-full h-full object-cover"
              />
            </div>

            {/* 桌面端浮动卡片 */}
            <div className="hidden md:block">
              {[
                { pos: '-top-8 left-8', icon: 'ri-car-line', iconBg: 'bg-[#1E88E5]/10', iconColor: 'text-[#1E88E5]', value: '156', label: '今日出车趟数', delay: '0.4s' },
                { pos: '-top-8 right-8', icon: 'ri-map-pin-line', iconBg: 'bg-[#00E5FF]/10', iconColor: 'text-[#00E5FF]', value: '8,542', label: '今日行驶里程(km)', delay: '0.5s' },
                { pos: '-bottom-8 left-8', icon: 'ri-gas-station-fill', iconBg: 'bg-orange-100', iconColor: 'text-orange-600', value: '¥24,680', label: '本月加油费用', delay: '0.6s' },
                { pos: '-bottom-8 right-8', icon: 'ri-tools-fill', iconBg: 'bg-pink-100', iconColor: 'text-pink-600', value: '¥18,320', label: '本月维修费用', delay: '0.7s' },
              ].map((card, i) => (
                <div key={i} className={`absolute ${card.pos} glass-card rounded-2xl p-6 shadow-xl transition-all duration-700 hover:scale-105 hover:shadow-2xl ${dashboardAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: card.delay }}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 flex items-center justify-center ${card.iconBg} rounded-xl`}>
                      <i className={`${card.icon} text-3xl text-[#1E88E5]`}></i>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-gray-900">{card.value}</div>
                      <div className="text-sm text-gray-600">{card.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 手机端四个卡片网格布局 */}
            <div className="grid grid-cols-2 gap-4 mt-6 md:hidden">
              {[
                { icon: 'ri-car-line', iconBg: 'bg-[#1E88E5]/10', value: '156', label: '今日出车趟数', delay: '0.4s' },
                { icon: 'ri-map-pin-line', iconBg: 'bg-[#00E5FF]/10', value: '8,542', label: '今日行驶里程(km)', delay: '0.5s' },
                { icon: 'ri-gas-station-fill', iconBg: 'bg-orange-100', value: '¥24,680', label: '本月加油费用', delay: '0.6s' },
                { icon: 'ri-tools-fill', iconBg: 'bg-pink-100', value: '¥18,320', label: '本月维修费用', delay: '0.7s' },
              ].map((card, i) => (
                <div key={i} className={`glass-card rounded-2xl p-4 shadow-md transition-all duration-700 ${dashboardAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: card.delay }}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center ${card.iconBg} rounded-xl flex-shrink-0`}>
                      <i className={`${card.icon} text-xl text-[#1E88E5]`}></i>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{card.value}</div>
                      <div className="text-xs text-gray-600 leading-tight">{card.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24">
            {[
              { icon: 'ri-refresh-line', label: '实时更新' },
              { icon: 'ri-bar-chart-box-line', label: '多维分析' },
              { icon: 'ri-line-chart-line', label: '趋势预测' },
              { icon: 'ri-device-line', label: '双端同步' },
            ].map((item, i) => (
              <div key={i} className={`text-center group transition-all duration-600 ${dashboardAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${0.8 + i * 0.1}s` }}>
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-white/60 rounded-xl transition-all duration-300 group-hover:bg-[#1E88E5]/20 group-hover:scale-110 shadow-sm">
                  <i className={`${item.icon} text-3xl text-[#1E88E5]`}></i>
                </div>
                <div className="text-[#0A2463] font-medium">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Experience */}
      <section id="mobile" className="py-24 px-6 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E88E5]/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00E5FF]/5 rounded-full blur-3xl pointer-events-none"></div>
        <div ref={mobileAnim.ref} className="max-w-7xl mx-auto relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${mobileAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.5s' }}>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">随时随地，掌控车队</h2>
            <p className="text-xl text-gray-600">iOS &amp; Android 双平台支持</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 items-end justify-items-center">
            {[
              { src: 'images/19-image.jpg', title: '司机端', desc: '行车日志 · 维修申请 · 派单接收', delay: '0s', mt: '' },
              { src: 'images/20-image.jpg', title: '管理端', desc: '数据看板 · 审批管理 · 智能调度', delay: '0.15s', mt: 'md:-mt-8' },
              { src: 'images/21-image.jpg', title: '乘客端', desc: '班车查询 · 实时位置 · 发车提醒', delay: '0.3s', mt: '' },
            ].map((phone, i) => (
              <div key={i} className={`text-center ${phone.mt} transition-all duration-800 ${mobileAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
                style={{ transitionDelay: phone.delay, transitionDuration: '0.8s' }}>
                <div className="relative w-64 h-auto mb-6 group animate-float" style={{ animationDelay: `${i * 0.8}s` }}>
                  <img src={phone.src} alt={phone.title}
                    className="w-full h-auto rounded-3xl shadow-2xl transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#1E88E5]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{phone.title}</h3>
                <p className="text-gray-600">{phone.desc}</p>
              </div>
            ))}
          </div>

          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mt-16 transition-all duration-700 ${mobileAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.5s' }}>
            {[
              { icon: 'ri-apple-fill', sub: 'IOS', main: 'Downlaod', href: 'https://app.vmscl.com/prod-api/crm/app-version/downloadPage?platform=ios' },
              { icon: 'ri-android-fill', sub: 'Android', main: 'Download', href: 'https://app.vmscl.com/prod-api/crm/app-version/downloadPage' },
            ].map((btn, i) => (
              <a key={i} href={btn.href} className="inline-block cursor-pointer group">
                <div className="bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-[#1E88E5] transition-all duration-300 flex items-center gap-3 px-5 py-3 hover:scale-105 hover:shadow-lg">
                  <i className={`${btn.icon} text-2xl transition-transform duration-300 group-hover:scale-110`}></i>
                  <div className="text-left">
                    <div className="text-xs opacity-75">{btn.sub}</div>
                    <div className="text-base font-semibold">{btn.main}</div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-lines" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#1E88E5" strokeWidth="0.8"/>
              </pattern>
              <pattern id="dots" width="60" height="60" patternUnits="userSpaceOnUse">
                <circle cx="30" cy="30" r="1.5" fill="#1E88E5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-lines)"/>
            <rect width="100%" height="100%" fill="url(#dots)"/>
          </svg>
          <div className="absolute top-1/4 left-1/6 w-80 h-80 bg-[#1E88E5]/15 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/6 w-96 h-96 bg-[#00E5FF]/15 rounded-full blur-3xl animate-float-delay"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] border border-[#1E88E5]/20 rounded-full animate-spin-slow"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border border-[#00E5FF]/15 rounded-full animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '18s' }}></div>
        </div>

        <div ref={contactAnim.ref} className="max-w-4xl mx-auto relative z-10">
          <div className={`text-center mb-8 transition-all duration-700 ${contactAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A2463] mb-6 leading-tight">
              现在开始，让您的车队管理<br />告别"靠感觉"
            </h2>
            <p className="text-xl md:text-2xl text-[#1565C0] mb-4 font-medium leading-relaxed">
              每一升油、每一次维修、每一趟行车，<br />都应该有数据为证。
            </p>
            <p className="text-lg text-[#1E88E5] max-w-3xl mx-auto leading-relaxed">
              数智运联帮您把隐形成本变成可见数字，<br />把管理经验变成可复制流程。
            </p>
          </div>

          <div className={`grid md:grid-cols-3 gap-6 mb-12 transition-all duration-700 ${contactAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.15s' }}>
            {[
              { icon: 'ri-shield-check-line', title: '多租户部署', desc: '不同公司数据完全隔离' },
              { icon: 'ri-device-line', title: 'PC + Android APP', desc: '双端使用，随时随地管理' },
              { icon: 'ri-customer-service-2-line', title: '专属客服支持', desc: '快速上手培训，全程陪伴' },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 backdrop-blur-md rounded-2xl p-6 border border-[#1E88E5]/20 hover:bg-white/90 hover:border-[#1E88E5]/50 transition-all duration-300 hover:scale-105 hover:shadow-lg group">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1E88E5]/10 rounded-xl mb-4 transition-all duration-300 group-hover:bg-[#1E88E5]/20 group-hover:scale-110">
                  <i className={`${item.icon} text-2xl text-[#1E88E5]`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#0A2463] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className={`transition-all duration-800 ${contactAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            style={{ transitionDelay: '0.3s' }}>
            <form
              id="contact_form"
              data-readdy-form
              action="https://readdy.ai/api/form/d6qmhjc4k19g20dvrc10"
              method="POST"
              className="bg-white/80 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-[#1E88E5]/20 shadow-xl"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                const company = formData.get('company') as string;
                const contact = formData.get('contact') as string;
                const phone = formData.get('phone') as string;
                const fleet_size = formData.get('fleet_size') as string;
                if (!company || !contact || !phone || !fleet_size) return;
                fetch('https://readdy.ai/api/form/d6qmhjc4k19g20dvrc10', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                  body: new URLSearchParams(formData as any).toString()
                }).then(response => {
                  if (response.ok) { form.reset(); }
                });
              }}
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {[
                  { label: '公司名称', name: 'company', type: 'text', placeholder: '请输入公司名称' },
                  { label: '联系人', name: 'contact', type: 'text', placeholder: '请输入联系人姓名' },
                  { label: '手机号码', name: 'phone', type: 'tel', placeholder: '请输入手机号码' },
                ].map((field, i) => (
                  <div key={i} className="group">
                    <label className="block text-[#0A2463] text-sm font-medium mb-2">{field.label} <span className="text-[#FF6D00]">*</span></label>
                    <input type={field.type} name={field.name} required placeholder={field.placeholder}
                      className="w-full px-4 py-3 bg-white border border-[#1E88E5]/30 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:shadow-md transition-all duration-300 text-sm" />
                  </div>
                ))}
                <div>
                  <label className="block text-[#0A2463] text-sm font-medium mb-2">车队规模 <span className="text-[#FF6D00]">*</span></label>
                  <select name="fleet_size" required
                    className="w-full px-4 py-3 bg-white border border-[#1E88E5]/30 rounded-xl text-gray-800 focus:outline-none focus:border-[#1E88E5] transition-all duration-300 cursor-pointer text-sm">
                    <option value="">请选择车队规模</option>
                    <option value="10辆以下">10辆以下</option>
                    <option value="10-50辆">10-50辆</option>
                    <option value="50-100辆">50-100辆</option>
                    <option value="100辆以上">100辆以上</option>
                  </select>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-[#0A2463] text-sm font-medium mb-2">需求说明</label>
                <textarea name="message" rows={4} maxLength={500}
                  className="w-full px-4 py-3 bg-white border border-[#1E88E5]/30 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:shadow-md transition-all duration-300 resize-none text-sm"
                  placeholder="请简要描述您的需求(限500字)"></textarea>
              </div>
              <button type="submit"
                className="w-full py-4 bg-[#1E88E5] text-white text-base font-semibold rounded-xl hover:bg-[#0A2463] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02] hover:shadow-2xl group">
                立即免费申请体验
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </button>
            </form>
          </div>

          <div className={`grid md:grid-cols-3 gap-8 mt-16 text-center transition-all duration-700 ${contactAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.5s' }}>
            {[
              { icon: 'ri-phone-line', label: '咨询热线', value: '17612480158' },
              { icon: 'ri-mail-line', label: '商务邮箱', value: '3596325@qq.com' },
              { icon: 'ri-wechat-line', label: '微信客服', value: '微信同手机号' },
            ].map((item, i) => (
              <div key={i} className="group">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-[#1E88E5]/10 rounded-xl transition-all duration-300 group-hover:bg-[#1E88E5]/20 group-hover:scale-110">
                  <i className={`${item.icon} text-2xl text-[#1E88E5]`}></i>
                </div>
                <div className="text-[#0A2463] font-medium mb-1">{item.label}</div>
                <div className="text-gray-600 text-sm">{item.value}</div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-10 transition-all duration-700 ${contactAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.6s' }}>
            <button onClick={() => scrollToSection('overview')}
              className="px-8 py-3 bg-transparent border-2 border-[#1E88E5]/60 text-[#1E88E5] text-base font-medium rounded-full hover:bg-[#1E88E5]/10 hover:border-[#1E88E5] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2 group">
              联系我们了解详情
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-700 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-6">
                <img src="images/logo.png" alt="数智运联" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">数智运联</span>
              </div>
              <p className="text-gray-400 text-sm">专注车队数字化管理，让运营更简单</p>
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-white font-semibold mb-4">产品</h3>
              <ul className="space-y-3">
                {['功能介绍','价格方案','客户案例','帮助文档'].map(item => (
                  <li key={item}><a href="#features" className="text-gray-400 text-sm hover:text-[#00E5FF] transition-colors cursor-pointer">{item}</a></li>
                ))}
              </ul>
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-white font-semibold mb-4">关于</h3>
              <ul className="space-y-3">
                {['公司介绍','联系我们','加入我们','隐私政策'].map(item => (
                  <li key={item}><a href="#contact" className="text-gray-400 text-sm hover:text-[#00E5FF] transition-colors cursor-pointer">{item}</a></li>
                ))}
              </ul>
            </div>
            <div className="col-span-2 md:col-span-1 text-center md:text-left">
              <h3 className="text-white font-semibold mb-4">关注我们</h3>
              <p className="text-gray-400 text-sm mb-4">订阅获取最新产品动态</p>
              <div className="flex gap-2 max-w-xs mx-auto md:mx-0">
                <input type="email" placeholder="输入邮箱地址"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E5FF] transition-colors" />
                <button className="w-10 h-10 flex items-center justify-center bg-[#1E88E5] rounded-lg hover:bg-[#00E5FF] transition-all duration-300 hover:scale-110 cursor-pointer flex-shrink-0">
                  <i className="ri-arrow-right-line text-white"></i>
                </button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-500 text-sm">© 2024 数智运联. All rights reserved.</p>
            <p className="text-gray-500 text-sm mt-2">版权所有：沈阳昊瑞德科技有限公司   辽ICP备2026003202号</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Feature Sell Point Component
function FeatureSellPoint({ point }: {
  point: {
    title: string;
    subtitle: string;
    desc: string;
    image: string;
    features: string[];
    reverse: boolean;
  };
}) {
  const { ref, inView } = useInView(0.1);

  return (
    <div ref={ref} className={`grid md:grid-cols-2 gap-12 items-center ${point.reverse ? 'md:grid-flow-dense' : ''}`}>
      {/* Image Side */}
      <div
        className={`${point.reverse ? 'md:col-start-2' : ''}`}
        style={{
          transition: 'opacity 1.6s cubic-bezier(0.16, 1, 0.3, 1), transform 1.6s cubic-bezier(0.16, 1, 0.3, 1)',
          transitionDelay: '0.1s',
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateX(0)' : `translateX(${point.reverse ? '60px' : '-60px'})`,
        }}
      >
        <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
          <img
            src={point.image}
            alt={point.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2463]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        </div>
      </div>

      {/* Content Side */}
      <div
        className={`${point.reverse ? 'md:col-start-1 md:row-start-1' : ''}`}
        style={{
          transition: 'opacity 1.6s cubic-bezier(0.16, 1, 0.3, 1), transform 1.6s cubic-bezier(0.16, 1, 0.3, 1)',
          transitionDelay: '0.28s',
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateX(0)' : `translateX(${point.reverse ? '-60px' : '60px'})`,
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-pulse"></span>
          <span className="w-1.5 h-1.5 bg-[#1E88E5] rounded-full"></span>
        </div>

        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          {point.title}
        </h3>
        <p className="text-xl text-[#1E88E5] font-medium mb-6">
          {point.subtitle}
        </p>
        <p className="text-base text-gray-600 leading-relaxed mb-8">
          {point.desc}
        </p>

        <div className="grid grid-cols-2 gap-3">
          {point.features.map((feature, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-[#0A2463]/5 to-[#1E88E5]/5 rounded-xl border border-[#1E88E5]/20 hover:border-[#1E88E5]/50 hover:shadow-lg hover:scale-105"
              style={{
                transition: `opacity 1.4s cubic-bezier(0.16, 1, 0.3, 1), transform 1.4s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s, box-shadow 0.3s`,
                transitionDelay: `${0.42 + i * 0.15}s`,
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(20px)',
              }}
            >
              <div className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full flex-shrink-0"></div>
              <span className="text-sm font-medium text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
